import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import { PLAY_STORE_URL } from '../components/GooglePlayBadge';
import { TermsOfService, Disclaimer } from './LegalContent';

type SupportTab = 'about' | 'what-sets-us-apart' | 'who-we-are' | 'how-we-score' | 'free-vs-plus' | 'google-auth' | 'privacy' | 'terms' | 'disclaimer';

function SupportPage() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<SupportTab>('about');

  useEffect(() => {
    const state = location.state as { tab?: SupportTab } | null;
    if (state?.tab) {
      setActiveTab(state.tab);
    }
  }, [location.state]);

  return (
    <div className="support-page">
      <div className="support-card">
        <h2>Support</h2>
        <div className="support-nav">
          <button
            className={`support-nav-link ${activeTab === 'about' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'about' ? 'true' : undefined}
            onClick={() => setActiveTab('about')}
          >
            About StandLogic
          </button>
          <button
            className={`support-nav-link ${activeTab === 'what-sets-us-apart' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'what-sets-us-apart' ? 'true' : undefined}
            onClick={() => setActiveTab('what-sets-us-apart')}
          >
            What Sets Us Apart
          </button>
          <button
            className={`support-nav-link ${activeTab === 'who-we-are' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'who-we-are' ? 'true' : undefined}
            onClick={() => setActiveTab('who-we-are')}
          >
            Who We Are
          </button>
          <button
            className={`support-nav-link ${activeTab === 'how-we-score' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'how-we-score' ? 'true' : undefined}
            onClick={() => setActiveTab('how-we-score')}
          >
            How We Score
          </button>
          <button
            className={`support-nav-link ${activeTab === 'free-vs-plus' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'free-vs-plus' ? 'true' : undefined}
            onClick={() => setActiveTab('free-vs-plus')}
          >
            Free vs Plus
          </button>
          <button
            className={`support-nav-link ${activeTab === 'google-auth' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'google-auth' ? 'true' : undefined}
            onClick={() => setActiveTab('google-auth')}
          >
            Google Authentication
          </button>
          <button
            className={`support-nav-link ${activeTab === 'privacy' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'privacy' ? 'true' : undefined}
            onClick={() => setActiveTab('privacy')}
          >
            Privacy Policy
          </button>
          <button
            className={`support-nav-link ${activeTab === 'terms' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'terms' ? 'true' : undefined}
            onClick={() => setActiveTab('terms')}
          >
            Terms of Service
          </button>
          <button
            className={`support-nav-link ${activeTab === 'disclaimer' ? 'support-nav-link-active' : ''}`}
            aria-current={activeTab === 'disclaimer' ? 'true' : undefined}
            onClick={() => setActiveTab('disclaimer')}
          >
            Disclaimer
          </button>
        </div>
      </div>

      {activeTab === 'about' && <AboutOurScoring />}
      {activeTab === 'what-sets-us-apart' && <WhatSetsUsApart />}
      {activeTab === 'who-we-are' && <WhoWeAre />}
      {activeTab === 'free-vs-plus' && <FreeVsPlus />}
      {activeTab === 'google-auth' && <GoogleAuth />}
      {activeTab === 'privacy' && <PrivacyPolicy />}
      {activeTab === 'terms' && <TermsOfService />}
      {activeTab === 'disclaimer' && <Disclaimer />}
      {activeTab === 'how-we-score' && <HowWeScore />}
    </div>
  );
}

function AboutOurScoring() {
  return (
    <div className="support-card">
      <h3>About StandLogic</h3>
      <p className="support-intro">
        StandLogic is built entirely on publicly accessible information — open weather data, established lunar cycle calculations, and decades of published research on whitetail deer behavior. Our goal is simple: bring the best of all available information together in one place so hunters can make more informed decisions about when to be in the stand.
      </p>

      <div className="support-section">
        <h4>What We Do</h4>
        <p>
          StandLogic synthesizes weather forecasts, barometric pressure patterns, wind analysis, and lunar data into a single, easy-to-read score for each hunting period. Our scoring model weighs temperature, fronts, wind, precipitation, cloud cover, pressure trends, and moon phase to help you identify more favorable hunting conditions.
        </p>
      </div>

      <div className="support-section">
        <h4>Our Sources</h4>
        <p>
          All data feeding our logic comes from publicly available sources, and we're open about what goes in. Weather forecasts are sourced from open meteorological APIs that provide hourly temperature, wind, pressure, cloud cover, and precipitation data. Lunar calculations use well-established astronomical algorithms. How we combine and weight that data is our own method.
        </p>
      </div>

      <div className="support-section">
        <h4>What This Is Not</h4>
        <p>
          StandLogic is not a guarantee. Deer are living animals in complex ecosystems, and their behavior is influenced by countless variables that no scoring system can fully capture — local hunting pressure, food source availability, individual deer personality, terrain features, human activity patterns, and factors researchers haven't yet identified. A 5-star morning doesn't mean you'll see a deer, and a 2-star evening doesn't mean you won't.
        </p>
      </div>

      <div className="support-section">
        <h4>Our Commitment</h4>
        <p>
          This is our best version of all the information available. We continuously evaluate our logic against real-world observations and published research. The goal isn't perfection — it's giving hunters the best possible data to make decisions about which mornings to set the alarm and which evenings to stay in the stand an extra hour.
        </p>
        <p>
          At the end of the day, you still have to read the woods, know your property, and make the call. StandLogic just helps you pick the days where the odds tilt a little more in your favor.
        </p>
      </div>
    </div>
  );
}

function WhatSetsUsApart() {
  return (
    <div className="support-card">
      <h3>What Sets Us Apart</h3>
      <p className="support-intro">
        StandLogic does one thing: it turns the forecast into a simple score for when deer are most likely to be headed to feed. There's no hunting advice, no season planning, and no extras. You get the data and decide for yourself.
      </p>

      <div className="support-section">
        <h4>Just the Data</h4>
        <p>
          We take public weather data and lunar timing and turn them into a morning and an evening score for each day. The score comes from our proprietary combination and weighting of that data. That's all it does.
        </p>
      </div>

      <div className="support-section">
        <h4>Feeding-Time Analysis, Nothing Else</h4>
        <p>
          The score looks at one question: do conditions favor deer movement during the hours you'd be in the stand? It looks at temperature, wind, pressure changes, precipitation, cloud cover, moon timing and seasonal timing. We don't give rut advice or calendars, and we leave out herd management and tactics, which are well covered elsewhere.
        </p>
      </div>

      <div className="support-section">
        <h4>Adjusted for Where You Hunt</h4>
        <p>
          Deer in Texas and deer in Wisconsin respond to the same weather differently, so the thresholds adjust automatically based on where you hunt. There's nothing to set or learn.
        </p>
      </div>

      <div className="support-section">
        <h4>Quick to Read</h4>
        <p>
          Open it, check the next few days, and close it. A star rating per hunt period, with a breakdown if you want it.
        </p>
      </div>

      <div className="support-section">
        <h4>No Ads. Ever.</h4>
        <p>
          No ads, even on the free plan. The last thing you need on a hunt is a bright, flashing pop-up lighting up your screen and spooking a deer. We keep it simple on purpose.
        </p>
      </div>

      <div className="support-section">
        <h4>Priced Accordingly</h4>
        <p>
          Because we keep it simple and to the point, it costs less. You aren't paying for maps, journals, trail cameras, or a large feature set you won't use. If you want a quick forecast, that's what the plan covers.
        </p>
      </div>

      <div className="support-section">
        <h4>Honest About Limits</h4>
        <p>
          Scores are estimates, not promises. Deer are unpredictable, and a good score isn't a guarantee. See the Disclaimer.
        </p>
      </div>
    </div>
  );
}

function WhoWeAre() {
  return (
    <div className="support-card">
      <h3>Who We Are</h3>

      <div className="support-section">
        <p>
          We're not professional hunters. We don't have sponsors, we don't hunt giant ranches, and we don't post grip-and-grin photos with 200-inch bucks every November. Nobody's paying us to be in a treestand.
        </p>
      </div>

      <div className="support-section">
        <h4>How It Started</h4>
        <p>
          With the power of AI, we realized we had a way to turn large amounts of data into usable information. We began feeding our hunting observations into a system that combined existing research with what we were seeing in the field. We wanted to understand why: Why did we see deer feeding all day yesterday, but none today? As the data came together, we decided to turn our algorithm into something we could take into the woods with us.
        </p>
      </div>

      <div className="support-section">
        <p>
          We built StandLogic because we wanted this tool for ourselves. We were already checking five apps, cross-referencing weather forecasts with moon phases, and trying to remember which conditions produced our best sits last season. We figured if we could turn that process into something automatic — something that just tells you "Tuesday morning looks really good" — it would help us make the most of the limited time we actually get to hunt. And just as important, we didn't want to pay for a whole bunch of features we didn't really want. So we built the simple version.
        </p>
      </div>

      <div className="support-section">
        <h4>We hunt deer. For real.</h4>
        <p>
          We hunt in southwest Pennsylvania. We don't see monster bucks. We've never had a trail camera photo that would make a magazine cover. If one of us gets a nice 8-point, we're texting the group chat at 6:45 AM and nobody's getting any work done that day.
        </p>
      </div>

      <div className="support-section">
        <p>
          That's what this is. Regular hunters who happen to love data, building something that helps other regular hunters make smarter decisions with the time they have.
        </p>
      </div>
    </div>
  );
}

function FreeVsPlus() {
  return (
    <div className="support-card">
      <h3>Free vs Plus</h3>
      <p className="support-intro">
        StandLogic has two account levels. Free accounts get accurate, weather-based scoring for the next three days.
        Plus accounts see the full seven-day outlook and get hourly weather detail further ahead.
      </p>

      <div className="support-section">
        <h4>Side-by-Side</h4>
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th>Plus</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>7-Day Outlook</td>
                <td>Days 1–3. Days 4–7 are locked.</td>
                <td>All 7 days, including today</td>
              </tr>
              <tr>
                <td>Hourly (Daily) View</td>
                <td>Next 3 days</td>
                <td>Next 7 days with weather detail</td>
              </tr>
              <tr>
                <td>Monthly Calendar</td>
                <td>Full calendar. Weather is factored in for the next 3 days only.</td>
                <td>Full calendar with weather factored in for the next 7 days</td>
              </tr>
              <tr>
                <td>Saved Locations</td>
                <td>One zipcode</td>
                <td>Up to 5 named zipcodes, switchable from the dashboard</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="support-section">
        <h4>Why Are Some Days Locked?</h4>
        <p>
          Weather forecasts are most reliable in the near term and the data behind them is what costs us to deliver.
          Free accounts get the full scoring experience for the next three days. Days beyond that appear as locked
          cards on your 7-day outlook, and the hourly view for those days is only available with Plus.
        </p>
        <p>
          Days 6 and 7 are longer-range forecasts. Conditions and hunting scores can change as those dates
          approach, so check again closer to your hunt.
        </p>
      </div>

      <div className="support-section">
        <h4>What You Still See on the Monthly Calendar</h4>
        <p>
          Every day of the month still appears on the calendar. For days past your three-day window, the rating is
          based on moon phase and lunar feeding alignment only, without weather. Treat those as a rough guide rather
          than a full forecast. Days marked with a moon icon use moon data only, and days marked with a cloud icon
          include weather.
        </p>
      </div>

      <div className="support-section">
        <h4>How Do I Get Plus?</h4>
        <HowToGetPlus />
      </div>
    </div>
  );
}

function HowToGetPlus() {
  const { isAuthenticated } = useAuth();
  if (!Capacitor.isNativePlatform()) {
    return (
      <>
        <p>
          Plus is available through the StandLogic Android app on{' '}
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Google Play</a>. Install the app, sign in
          with Google, then subscribe monthly or annually from the Plus Subscription section of your Profile page.
          You can manage or cancel your subscription anytime in Google Play.
        </p>
        <a className="btn btn-primary support-subscribe-cta" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
          Get StandLogic on Google Play
        </a>
      </>
    );
  }
  return (
    <>
      <p>
        Subscribe monthly or annually through Google Play from the Plus Subscription section of your{' '}
        {isAuthenticated ? 'Profile page' : 'Profile page after you sign in'}.
        Plus is available as soon as Google Play confirms your purchase, and you can manage or cancel it
        anytime in Google Play.
      </p>
      <Link className="btn btn-primary support-subscribe-cta" to={isAuthenticated ? '/profile' : '/signin'}>
        {isAuthenticated ? 'Go to Profile to Subscribe' : 'Sign In to Get Plus'}
      </Link>
    </>
  );
}
function GoogleAuth() {
  return (
    <div className="support-card">
      <h3>Google Authentication</h3>

      <div className="support-section">
        <h4>Why Google?</h4>
        <p>
          StandLogic uses Google Authentication exclusively. We don't store your password. We don't manage your credentials. We don't have a database full of email/password combinations that could be compromised. When you sign in, you're authenticating directly with Google — one of the most secure identity platforms in the world.
        </p>
      </div>

      <div className="support-section">
        <h4>What we collect</h4>
        <p>
          When you sign in with Google, we receive your email address. That's it. We don't get your name, your Google password, your contacts, your calendar, your photos, or anything else. We use your email to identify your account, and that's the extent of it.
        </p>
        <p>
          The only additional information we store is what you voluntarily provide: your nickname and hunting zipcode(s). All of that is editable in your profile at any time.
        </p>
      </div>

      <div className="support-section">
        <h4>Don't trust us. Trust Google.</h4>
        <p>
          We're a small team of hunters who built a scoring app. We're not a security company. We don't want the responsibility of protecting your credentials, and you shouldn't want us to have them. By using Google Authentication, your login security is handled by a company that spends billions of dollars on it — not by us.
        </p>
        <p>
          If you ever want to revoke StandLogic's access, you can do that directly from your Google account settings at any time. No need to contact us, no need to request account deletion. You're in control.
        </p>
      </div>

      <div className="support-section">
        <h4>How to Revoke Access</h4>
        <ol className="support-steps">
          <li>Go to your <a href="https://myaccount.google.com" target="_blank" rel="noopener noreferrer">Google Account Dashboard</a>.</li>
          <li>Click <strong>Security</strong> on the left-side navigation menu.</li>
          <li>Scroll down to the "Your connections to third-party apps &amp; services" section.</li>
          <li>Click <strong>See all connections</strong> to view everything with access.</li>
          <li>Locate <strong>StandLogic</strong> and remove it.</li>
        </ol>
        <p>
          That's it. Once removed, StandLogic can no longer access your Google account information and you'll be signed out the next time you visit.
        </p>
      </div>
    </div>
  );
}

export const PRIVACY_CONTACT_EMAIL = 'standlogicapp@gmail.com';

export function PrivacyPolicy() {
  return (
    <div className="support-card">
      <h3>Privacy Policy</h3>
      <p className="support-intro" style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>Last updated: October 2026</p>
      <p className="support-intro">
        This policy explains what information StandLogic (the website and the Android app) collects, why, and the choices you have. We built StandLogic to collect as little as possible.
      </p>

      <div className="support-section">
        <h4>What We Collect</h4>
        <p>
          When you sign in with Google, we receive your email address. That's the only information Google provides to us.
        </p>
        <p>
          After signing in, you voluntarily provide:
        </p>
        <ul className="support-list">
          <li>A nickname (a display name of your choosing)</li>
          <li>A 5-digit US zipcode (to generate location-specific forecasts). Plus members may save up to 5 zipcodes, each with a name they choose.</li>
        </ul>
        <p>
          We also store your plan level (Free or Plus).
        </p>
      </div>

      <div className="support-section">
        <h4>Information Stored on Your Device</h4>
        <p>
          The website and app keep your sign-in session and your profile in your browser or app storage so you stay signed in. The production website also stores your analytics preference locally. The Android app does not use analytics or advertising SDKs.
        </p>
        <p>
          On standlogic.app, Google Analytics loads only if you accept optional analytics cookies.
          It uses cookies to measure website visits and sends usage information, such as the pages
          visited and browser and device information, to Google. We do not send your account
          information, hunting locations, URL query strings, or sign-in callback URLs in our
          page-view events, and advertising personalization is disabled.
          You can decline without losing access to the site and change your choice using
          Analytics preferences at the bottom of any production website page. Declining stops future
          analytics collection; it does not erase information already sent to Google.
          Previously set analytics cookies may remain until they expire or you clear them in your browser.
          See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's privacy policy</a> for
          information about Google's handling of this data.
        </p>
      </div>

      <div className="support-section">
        <h4>Technical Logs</h4>
        <p>
          Our servers run on AWS and keep standard operational logs (such as request time, the page or API endpoint requested, and IP address) for troubleshooting and security. These logs are kept for up to 90 days, then deleted automatically, and are not used for profiling or advertising.
        </p>
      </div>

      <div className="support-section">
        <h4>What We Do NOT Collect</h4>
        <ul className="support-list">
          <li>Passwords (we use Google Authentication exclusively)</li>
          <li>Your precise location or GPS data. The app only requests internet access.</li>
          <li>Phone number or physical address</li>
          <li>Full payment card numbers or bank details (Google Play handles payment details)</li>
          <li>Google contacts, calendar, or any other Google account data</li>
          <li>Device identifiers or advertising IDs</li>
        </ul>
      </div>

      <div className="support-section">
        <h4>How We Use Your Information</h4>
        <ul className="support-list">
          <li><strong>Email</strong> — To identify your account and, if applicable, communicate account-related information.</li>
          <li><strong>Zipcode</strong> — To retrieve weather forecast data for your hunting area, calculate sunrise/sunset times, and tune scoring to your part of the country.</li>
          <li><strong>Nickname</strong> — Displayed within the app for personalization. Never shown to other users.</li>
          <li><strong>Plan level</strong> — To decide which features and forecast days are available to you.</li>
        </ul>
      </div>

      <div className="support-section">
        <h4>How We Store Your Information</h4>
        <p>
          Your profile is stored in AWS Cognito, Amazon's managed identity service, in the United States. For subscriptions, we store Google Play purchase tokens, subscription status and paid-through dates in AWS DynamoDB to verify purchases and manage access. Data is encrypted in transit and at rest by AWS. We do not store payment card numbers.
        </p>
      </div>

      <div className="support-section">
        <h4>Third-Party Services</h4>
        <ul className="support-list">
          <li><strong>Google Authentication</strong> — Handles your sign-in. Google's privacy policy governs what Google collects during authentication.</li>
          <li><strong>Amazon Web Services</strong> — Hosts the app, stores account data and processes requests on our behalf.</li>
          <li><strong>Google Play Billing</strong> — Processes Plus subscription purchases and renewals in the Android app. Google provides purchase tokens and subscription status so we can verify purchases and manage access. Google Play handles payment details directly.</li>
          <li><strong>WeatherAPI.com</strong> — We send the approximate coordinates of your zipcode (not your zipcode, email or identity) to WeatherAPI.com to retrieve weather data. Weather data provided by <a href="https://www.weatherapi.com/" target="_blank" rel="noopener noreferrer">WeatherAPI.com</a>.</li>
          <li><strong>Zippopotam.us</strong> — We send your zipcode to convert it into a city, state and coordinates. No other personal information is sent.</li>
        </ul>
      </div>

      <div className="support-section">
        <h4>What We Don't Do</h4>
        <ul className="support-list">
          <li>We don't sell your data.</li>
          <li>We don't share your information with advertisers.</li>
          <li>We don't track you across other websites or apps.</li>
          <li>We don't send marketing emails.</li>
          <li>We don't build profiles about you beyond what you explicitly provide.</li>
        </ul>
      </div>

      <div className="support-section">
        <h4>Data Retention and Deletion</h4>
        <p>
          Your data is kept as long as your account is active. Free accounts that have had no sign-in activity for an extended period (at least 90 days) may be deleted by an administrator. You can delete your account and all associated data (email, nickname, zipcodes and plan) yourself at any time from your profile. You can also ask us to delete it by contacting us at the address below, and we will do so within 30 days. Step-by-step instructions are on our <Link to="/delete-account">account deletion page</Link>. Revoking StandLogic's access in your Google account settings also severs the sign-in link.
        </p>
        <p>
           For Google Play subscriptions, cancellation through Google Play stops renewal and preserves access through
           the paid period. Account deletion requests cancellation of renewal and removes StandLogic access immediately;
           it does not issue a refund. We retain a hashed purchase-token reference and account identifier for 90 days
           to handle delayed notifications, then schedule them for automatic deletion. Google Play retains its own
           transaction records under its policies.
        </p>
      </div>

      <div className="support-section">
        <h4>Your Rights</h4>
        <ul className="support-list">
          <li>You can view and edit your nickname and zipcode(s) on your Profile page at any time.</li>
          <li>You can revoke Google access from your Google Account Dashboard.</li>
          <li>You can request a copy of your data or its deletion by contacting us. We extend these rights to all users, including residents of states with privacy laws such as California.</li>
        </ul>
      </div>

      <div className="support-section">
        <h4>Children</h4>
        <p>
          StandLogic is not directed to children under 13, and we do not knowingly collect information from them. If you believe a child has provided us information, contact us and we will delete it.
        </p>
      </div>

      <div className="support-section">
        <h4>Changes to This Policy</h4>
        <p>
          If we change what we collect or how we use it (for example, when paid plans are introduced and a payment provider is involved), we'll update this page and the date above.
        </p>
      </div>

      <div className="support-section">
        <h4>Contact</h4>
        <p>
          Questions or requests: <a href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</a>
        </p>
      </div>
    </div>
  );
}
function HowWeScore() {
  return (
    <div className="support-card">
      <h3>How We Score</h3>
      <p className="support-intro">
        StandLogic gives each morning and evening hunt period a 1–5 star rating for how likely deer are to be actively moving and feeding. We combine weather and lunar conditions, weigh each one for your area, and roll them into a single score so you can see at a glance when deer are most likely to be on the move during legal shooting hours.
      </p>

      <div className="support-section">
        <h4>🌡️ Temperature</h4>
        <p>
          Cooler temperatures drive deer movement, but "cool" means different things in different places. Our logic accounts for where you hunt. We also score <strong>temperature drops</strong> from the previous day, the trend during your sit, and the overnight swing, because a sudden change matters as much as the absolute number.
        </p>
      </div>

      <div className="support-section">
        <h4>🌊 Weather Fronts</h4>
        <p>
          We use three days (about 72 hours) of historical weather alongside the forecast to put changing conditions in context. We look for signs of a front, such as rising pressure paired with cooling temperatures or a sustained wind shift, when evaluating recovery after disruptive weather. Significant fronts can lower scores as disruptive weather suppresses deer activity. Once the front passes and conditions improve, the model gives a strong initial boost to reflect the potential rebound in movement and feeding. That boost gradually fades as conditions settle. How much a front matters depends on your region.
        </p>
      </div>

      <div className="support-section">
        <h4>💨 Wind</h4>
        <p>
          Wind affects both deer behavior and your ability to stay undetected. What counts as ideal wind varies by region, and our logic accounts for it. Excessive gustiness and swirling winds are penalized everywhere because they make scent control unpredictable. Strong, gusty winds can also make deer more nervous and cautious, making hunting more difficult.
        </p>
      </div>

      <div className="support-section">
        <h4>🌧️ Precipitation</h4>
        <p>
          Light rain ending before or during your hunt is a positive in most places, and heavy rain and thunderstorms score poorly. How rain and snow are treated depends on what's normal where you hunt.
        </p>
      </div>

      <div className="support-section">
        <h4>☁️ Cloud Cover</h4>
        <p>
          Overcast skies combined with cool temperatures make deer feel more secure moving during daylight. How much that matters depends on how common cloud cover is in your region.
        </p>
      </div>

      <div className="support-section">
        <h4>📊 Barometric Pressure</h4>
        <p>
          Short-term pressure trends signal changing weather. Rapidly rising pressure, typically after a front passes, scores highest. How much pressure changes count depends on your region.
        </p>
      </div>

      <div className="support-section">
        <h4>🌙 Moon / Solunar</h4>
        <p>
          The lunar cycle affects when deer feed regardless of region. During a <strong>new moon</strong> (dark nights), deer feed more during daylight hours — the best scenario for hunters. During a <strong>full moon</strong>, deer feed heavily overnight and are far less active during legal shooting hours. This factor applies consistently across all regions.
        </p>
      </div>

      <div className="support-section">
        <h4>⏰ Time Windows</h4>
        <p>
          The timing of our logic is the heart of StandLogic. We've done extensive analysis of deer feeding activity during the crepuscular periods around dawn and dusk, when deer are most active. Weather and lunar effects work together to explain why some days it seems like every deer in the area feeds at once, and other days they don't. Our logic is built around that, and it shifts with the seasons and your location so the score reflects the hours that matter where you hunt. It also knows you don't hunt in the dark, so sunrise and sunset hours are factored into your score.
        </p>
      </div>

      <div className="support-section">
        <h4>⭐ Final Rating</h4>
        <p>
          Each factor receives its own 1–5 score, then the factors are combined using weights tuned to your region. The weighted total is converted to your final star rating:
        </p>
        <ul className="support-list">
          <li><strong>5 Stars</strong> — Excellent. Multiple factors aligning for peak deer movement in your region.</li>
          <li><strong>4 Stars</strong> — Good. Conditions favor above-average activity for your local deer.</li>
          <li><strong>3 Stars</strong> — Average. Standard deer movement patterns expected.</li>
          <li><strong>2 Stars</strong> — Below Average. One or more factors suppressing activity.</li>
          <li><strong>1 Star</strong> — Poor. Conditions strongly discourage daylight deer movement.</li>
        </ul>
      </div>

      <div className="support-section">
        <h4>🔄 Why Did It Change?</h4>
        <p>
          You might see a 5-star morning coming up, then wake up and find it's a 3. That's the weather, and it's the nature of hunting deer. Forecasts shift as the day gets closer, and small changes in the weather can drastically change how our logic evaluates a given time period. The closer you get to your hunt, the more accurate the score becomes, so check back before you head out.
        </p>
      </div>

      <div className="support-section">
        <h4>📅 Beyond the Weather Forecast</h4>
        <p>
          Weather data is only available for approximately 7 days out. For dates beyond that range (visible on the Monthly View), scoring is based solely on the moon phase. These days are marked with a 🌙 icon to indicate the score is an estimate without weather context.
        </p>
      </div>
    </div>
  );
}

export default SupportPage;
